import type { Terminal } from '@xterm/xterm';
import { Fragment, memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ArrowUp, LockKeyhole, Settings2, Square } from 'lucide-react';
import { Button } from '../../components/Button.tsx';
import { Checkbox } from '../../components/Checkbox.tsx';
import { SelectField } from '../../components/SelectField.tsx';
import { message } from '../authorization/model.ts';
import { MAX_RENDERED_EVENTS } from '../engagement/model.ts';
import { withoutToolNames } from '../discovery/toolNames.ts';
import { api, ApiError, type ContainerPhase, type TerminalCapability } from '../onboarding/api.ts';
import styles from './WorkspaceTerminal.module.css';
import {
  WORKSPACE_QUESTIONS,
  questionLabel,
  questionText,
  readableAnswer,
  workspaceAnswer,
  type WorkspaceContext,
} from './workspaceQuestions.ts';
import {
  byTime,
  formatDiscoveryClock,
  type DiscoveryActivityEntry,
} from '../discovery/activityLog.ts';
import { LOG_LINE_ATTR, useCopyOnSelect } from './copyOnSelect.ts';
import { reasoningBullets } from './reasoningText.ts';
import { useTypedStream } from './typedStream.ts';

interface WorkspaceTerminalProps extends WorkspaceContext {
  /** Enables answers from this workspace's records; without it answers stay local. */
  workspaceId?: string;
  workspaceName: string;
  domain: string;
  phase: ContainerPhase;
  /** Live capability from the container view; null until the first live answer. */
  capability: TerminalCapability | null;
  authorizationId: string;
  setupSessionId: string;
  ownerToken: string;
  /** Called once after every end, so the capability can be refreshed. */
  onEnded: () => void;
  /** Runner/discovery activity projected into the designed stream. */
  activityEntries?: DiscoveryActivityEntry[];
  /** Pipeline step derived from discovery progress when the Kali session is not live. */
  pipelineStep?: number;
  /** Latest discovery attempt status for the title bar, e.g. "Running · step 3 of 6". */
  activityStatus?: { label: string; live: boolean } | null;
  /** The role can't take the terminal's write lease or ask: output only, with the reason. */
  readOnly?: boolean;
}

type Stage = 'idle' | 'opening' | 'live' | 'ended';

interface StreamLogEntry extends DiscoveryActivityEntry {
  isAnswer?: boolean;
  isReasoning?: boolean;
  rationale?: string;
  /** The answer is still being written. */
  pending?: boolean;
  /** An answer from an earlier visit: shown whole, not typed out again. */
  remembered?: boolean;
  /** Live model output (streamed thinking): rendered whole as it arrives, not typed. */
  live?: boolean;
  /** A reasoning block collapsed to a label + toggle until the operator expands it. */
  collapsible?: boolean;
}

/** How the answer check reads on an answer line. */
const verdict = (verified: boolean | null) => ({
  glyph: verified === false ? '!' : '·',
  tone: verified === false ? ('warn' as const) : ('info' as const),
  ref: verified === true ? 'Verified' : verified === false ? 'Unverified' : undefined,
});

// eslint-disable-next-line no-control-regex -- strip terminal control bytes before rendering text
const plainTerminalText = (text: string) => text.replace(/[\u0000-\u001f\u007f-\u009f]/g, ' ');

const GLYPH_LABEL: Record<string, string> = {
  '›': 'Command',
  '‹': 'Response',
  '·': 'Event',
  '✓': 'Done',
  '✕': 'Failed',
  '!': 'Attention',
  '?': 'Needs review',
  '⊘': 'Blocked by scope',
  '■': 'Stop',
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, Math.round(value)));

const MAX_PAUSED_OUTPUT = 1_048_576;
/** Scrolling this close to the top of the log loads earlier lines. */
const EARLIER_TRIGGER_PX = 48;

function exitText(frame: { code?: unknown; signal?: unknown }) {
  const code = typeof frame.code === 'number' ? ` with code ${frame.code}` : '';
  const signal = typeof frame.signal === 'string' && frame.signal ? ` (${frame.signal})` : '';
  return `Terminal exited${code}${signal}.`;
}

const PIPELINE_STAGES = [
  { id: 1, name: 'Scope', label: '1. Scope' },
  { id: 2, name: 'Discover', label: '2. Discover' },
  { id: 3, name: 'Map', label: '3. Map' },
  { id: 4, name: 'Probe', label: '4. Probe' },
  { id: 5, name: 'Proof', label: '5. Proof' },
  { id: 6, name: 'Monitor', label: '6. Monitor' },
];

interface EventRowProps {
  entry: StreamLogEntry;
  /** Characters of the text to show while the line is being typed. */
  length: number;
  typing: boolean;
}

/** One log line. Memoised so each typing tick redraws only the line being typed. */
const EventRow = memo(function EventRow({ entry, length, typing }: EventRowProps) {
  const [expanded, setExpanded] = useState(false);
  const collapsible = Boolean(entry.collapsible && entry.isReasoning && !entry.pending);
  return (
    <li
      className={styles.event}
      {...{ [LOG_LINE_ATTR]: '' }}
      data-tone={entry.tone}
      data-answer={entry.isAnswer || entry.pending || entry.remembered || undefined}
      data-reasoning={entry.isReasoning || entry.pending || undefined}
    >
      <time dateTime={entry.at}>{entry.time}</time>
      <span className={styles.glyph}>
        <span aria-hidden="true">{entry.glyph}</span>
        <span className={styles.visuallyHidden}>{GLYPH_LABEL[entry.glyph] ?? entry.glyph}</span>
      </span>
      <span className={styles.actor}>{entry.actor}</span>
      <span className={styles.eventText}>
        {(entry.pending || entry.isReasoning) && (
          <span className={styles.reasoningLabel}>
            {entry.reasoningLabel ?? (entry.pending ? 'Thinking' : 'Decision rationale')}
            {collapsible && (
              <button
                type="button"
                className={styles.reasoningToggle}
                aria-expanded={expanded}
                onClick={() => setExpanded((value) => !value)}
              >
                <span aria-hidden="true">{expanded ? '▾' : '▸'}</span> {expanded ? 'Hide' : 'Show'}
              </button>
            )}
          </span>
        )}
        {entry.rationale && (
          <>
            {entry.rationale}
            <span className={styles.reasoningLabel}>Answer</span>
          </>
        )}
        {typing ? (
          <>
            <span aria-hidden="true">
              {entry.text.slice(0, length)}
              <span className={styles.typewriterCursor} />
            </span>
            <span className={styles.visuallyHidden}>{entry.text}</span>
          </>
        ) : entry.pending ? (
          <>
            {entry.text}
            <span className={styles.thinking} aria-hidden="true" />
          </>
        ) : collapsible && !expanded ? null : collapsible ? (
          <ul className={styles.reasoningList}>
            {reasoningBullets(entry.text).map((bullet, index) => (
              <li key={index}>{bullet}</li>
            ))}
          </ul>
        ) : (
          entry.text
        )}
      </span>
      <span className={styles.refTag} data-ref={entry.ref}>
        {entry.ref}
      </span>
    </li>
  );
});

export function WorkspaceTerminal({
  workspaceName,
  domain,
  signedScopeDomains,
  phase,
  capability,
  authorizationId,
  setupSessionId,
  ownerToken,
  onEnded,
  container,
  authorizerName,
  authorizationConfirmedAt,
  workspaceId,
  activityEntries = [],
  pipelineStep,
  activityStatus,
  readOnly = false,
}: WorkspaceTerminalProps) {
  const [stage, setStage] = useState<Stage>('idle');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [stopping, setStopping] = useState(false);
  const [stoppedByUser, setStoppedByUser] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [fontSize, setFontSize] = useState('13');
  const [showTimestamps, setShowTimestamps] = useState(true);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<{ question: string; text: string } | null>(null);

  const questionRef = useRef<HTMLInputElement>(null);
  const pausedRef = useRef(false);
  const pendingOutputRef = useRef('');
  const truncatedRef = useRef(false);
  const fitRef = useRef<(() => void) | null>(null);
  const rootRef = useRef<HTMLElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLButtonElement>(null);
  const emptyRef = useRef<HTMLDivElement>(null);
  const interactedRef = useRef(false);
  const everReadyRef = useRef(false);
  const sentSizeRef = useRef({ cols: 0, rows: 0 });
  const termRef = useRef<Terminal | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const sessionIdRef = useRef('');
  const mountedRef = useRef(false);
  const readyRef = useRef(false);
  const endedRef = useRef(true);
  const stoppingRef = useRef(false);
  const socketClosedRef = useRef(true);
  const endStatusRef = useRef<string | null>(null);
  const onEndedRef = useRef(onEnded);

  // Questions and answers asked from this view, merged into the runner stream by time.
  const [entries, setEntries] = useState<StreamLogEntry[]>([]);
  // Follow new lines unless the reader has scrolled up to look at older ones.
  const stickRef = useRef(true);

  const ask = useCallback(
    (input: string) => {
      const clean = input.trim();
      if (!clean) return;
      setQuestion('');

      const askedAt = new Date().toISOString();
      const id = `${askedAt}-${Math.random()}`;
      const line = (
        entry: Omit<StreamLogEntry, 'id' | 'at' | 'time' | 'glyph' | 'actor'> & {
          glyph?: string;
          actor?: string;
        },
        at = askedAt,
      ): StreamLogEntry => ({
        id: `a-${id}`,
        at,
        time: formatDiscoveryClock(at),
        glyph: '·',
        actor: 'Traceira',
        ...entry,
      });
      const recorded = workspaceAnswer(clean, {
        domain,
        signedScopeDomains,
        phase,
        container,
        authorizerName,
        authorizationConfirmedAt,
      });
      const isWhy = /^\/?why\b/i.test(clean);
      const settle = (reply: StreamLogEntry) => {
        if (!mountedRef.current) return;
        setEntries((prev) => [...prev.filter((entry) => entry.id !== reply.id), reply]);
        setAnswer({ question: clean, text: reply.text });
        if (termRef.current && readyRef.current) {
          const response = reply.isReasoning
            ? `\r\n\x1b[36m┌─ ${reply.actor} · Decision rationale${reply.ref ? ` · ${reply.ref}` : ''} ─┐\x1b[0m\r\n│ ${plainTerminalText(reply.rationale ?? reply.text)}\r\n${reply.rationale ? `│ Answer: ${plainTerminalText(reply.text)}\r\n` : ''}└─\r\n`
            : `\r\n\x1b[36m· ${reply.actor}: \x1b[0m${plainTerminalText(reply.text)}\r\n`;
          termRef.current.write(`\r\n\x1b[32m› You: \x1b[0m${plainTerminalText(clean)}${response}`);
        }
      };

      // The answer lands at the tail, so asking brings the reader there.
      stickRef.current = true;
      setEntries((prev) => [
        ...prev,
        {
          id: `q-${id}`,
          at: askedAt,
          time: formatDiscoveryClock(askedAt),
          glyph: '›',
          actor: 'You',
          text: clean,
          tone: 'you',
        },
      ]);

      // /help lists commands; without a workspace id there is no record set to answer from.
      if (!workspaceId || clean.toLowerCase() === '/help') {
        settle(line({ text: recorded, tone: 'info', isAnswer: true }));
        return;
      }
      setEntries((prev) => [
        ...prev,
        line({
          text: 'Reviewing workspace scope, recent activity, and evidence before answering',
          tone: 'muted',
          actor: 'Traceira',
          ref: 'Thinking',
          pending: true,
        }),
      ]);
      if (termRef.current && readyRef.current && !pausedRef.current) {
        termRef.current.write(
          '\r\n\x1b[36m┌─ Traceira · Thinking ─┐\x1b[0m\r\n│ Reviewing workspace scope, recent activity, and evidence before answering…\r\n└─\r\n',
        );
      }
      const pendingId = `a-${id}`;
      let thinking = '';
      let finished = false;
      const finish = (reply: StreamLogEntry) => {
        if (finished) return;
        finished = true;
        settle(reply);
      };
      const updateThinking = (next: string) => {
        if (finished || !mountedRef.current) return;
        setEntries((prev) =>
          prev.map((entry) =>
            entry.id === pendingId && entry.pending
              ? { ...entry, text: next, live: true }
              : entry,
          ),
        );
      };
      api
        .askWorkspaceQuestionStream(
          workspaceId,
          questionText(clean),
          {
            onThinking: (delta) => {
              thinking += delta;
              updateThinking(thinking);
            },
            onAnswer: ({ answer: text, rationale, verified, answeredAt }) =>
              finish(
                line(
                  {
                    text: readableAnswer(text),
                    rationale,
                    ...verdict(verified),
                    isAnswer: true,
                    isReasoning: isWhy || Boolean(rationale),
                    actor: 'Traceira',
                  },
                  answeredAt ?? new Date().toISOString(),
                ),
              ),
            onError: () =>
              finish(
                line(
                  {
                    text: `The workspace assistant is not reachable right now. ${recorded}`,
                    tone: 'muted',
                    ref: 'Recorded',
                    isAnswer: true,
                  },
                  new Date().toISOString(),
                ),
              ),
          },
          setupSessionId,
          ownerToken,
        )
        .catch(() =>
          finish(
            line(
              {
                text: `The workspace assistant is not reachable right now. ${recorded}`,
                tone: 'muted',
                ref: 'Recorded',
                isAnswer: true,
              },
              new Date().toISOString(),
            ),
          ),
        );
    },
    [
      authorizationConfirmedAt,
      authorizerName,
      container,
      domain,
      signedScopeDomains,
      phase,
      workspaceId,
      setupSessionId,
      ownerToken,
    ],
  );

  // Memory: this person's earlier questions and answers in this workspace, merged in by time.
  useEffect(() => {
    if (!workspaceId) return;
    let current = true;
    api
      .getWorkspaceQuestions(workspaceId, setupSessionId, ownerToken)
      .then(({ turns }) => {
        if (!current) return;
        const remembered = turns.flatMap((turn): StreamLogEntry[] => {
          const answeredAt = turn.answeredAt ?? turn.askedAt;
          return [
            {
              id: `q-${turn.turnId}`,
              at: turn.askedAt,
              time: formatDiscoveryClock(turn.askedAt),
              glyph: '›',
              actor: 'You',
              text: questionLabel(turn.question),
              tone: 'you',
            },
            {
              id: `a-${turn.turnId}`,
              at: answeredAt,
              time: formatDiscoveryClock(answeredAt),
              actor: 'Traceira',
              text: readableAnswer(turn.answer),
              ...verdict(turn.verified),
              // Typed like every other line: shown whole it popped in ahead of the lines above it.
              isAnswer: true,
            },
          ];
        });
        setEntries((prev) => [...remembered, ...prev]);
      })
      // Without memory the terminal still answers; earlier turns just are not shown.
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [workspaceId, setupSessionId, ownerToken]);

  useEffect(() => {
    if (!termRef.current) return;
    termRef.current.options.fontSize = Number(fontSize);
    fitRef.current?.();
  }, [fontSize]);

  const flushOutput = () => {
    if (truncatedRef.current)
      termRef.current?.write(
        '\r\n[Earlier paused output omitted. Resume sooner to retain it.]\r\n',
      );
    if (pendingOutputRef.current) termRef.current?.write(pendingOutputRef.current);
    pendingOutputRef.current = '';
    truncatedRef.current = false;
  };

  const togglePause = () => {
    pausedRef.current = !pausedRef.current;
    setIsPaused(pausedRef.current);
    if (!pausedRef.current) flushOutput();
  };

  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);

  useEffect(() => {
    if (!interactedRef.current) return;
    const active = document.activeElement;
    if (
      !active ||
      active === document.body ||
      hostRef.current?.contains(active) ||
      (active instanceof HTMLButtonElement && active.disabled)
    )
      actionRef.current?.focus();
  }, [stage, stopping]);

  const release = (sessionId: string) =>
    api.stopTerminal(authorizationId, sessionId).catch(() => undefined);

  const teardown = () => {
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    observerRef.current?.disconnect();
    observerRef.current = null;
    const socket = socketRef.current;
    socketRef.current = null;
    if (socket && !socketClosedRef.current) socket.close();
    termRef.current?.dispose();
    termRef.current = null;
    fitRef.current = null;
    readyRef.current = false;
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      teardown();
    };
  }, []);

  const finish = (text: string) => {
    if (endedRef.current) return;
    endedRef.current = true;
    stoppingRef.current = false;
    readyRef.current = false;
    flushOutput();
    pausedRef.current = false;
    setIsPaused(false);
    if (termRef.current) termRef.current.options.disableStdin = true;
    const socket = socketRef.current;
    if (socket && !socketClosedRef.current) {
      socketClosedRef.current = true;
      socket.close();
    }
    setStopping(false);
    setStatus(text);
    setStage('ended');
    onEndedRef.current();
  };

  const open = async () => {
    const hadEnded = stage === 'ended';
    interactedRef.current = true;
    everReadyRef.current = false;
    teardown();
    hostRef.current?.replaceChildren();
    endedRef.current = false;
    stoppingRef.current = false;
    endStatusRef.current = null;
    sessionIdRef.current = '';
    setError(null);
    setStopping(false);
    setStoppedByUser(false);
    setIsPaused(false);
    pausedRef.current = false;
    pendingOutputRef.current = '';
    truncatedRef.current = false;
    setStatus('Opening terminal…');
    flushSync(() => setStage('opening'));
    try {
      const [{ Terminal: XTerm }, { FitAddon }] = await Promise.all([
        import('@xterm/xterm'),
        import('@xterm/addon-fit'),
        import('@xterm/xterm/css/xterm.css'),
      ]);
      const host = hostRef.current;
      if (!mountedRef.current || !host) return;

      const css = getComputedStyle(host);
      const token = (name: string) => css.getPropertyValue(name).trim() || undefined;
      const term = new XTerm({
        fontFamily: token('--font-mono'),
        fontSize: Number(fontSize),
        lineHeight: 1.45,
        cursorBlink: false,
        disableStdin: true,
        screenReaderMode: true,
        theme: {
          background: token('--color-bg-canvas'),
          foreground: token('--color-content-primary'),
          cursor: token('--color-content-primary'),
          selectionBackground: token('--color-bg-selected'),
        },
      });
      const fit = new FitAddon();
      term.loadAddon(fit);
      term.open(host);
      if (term.textarea) term.textarea.tabIndex = -1;
      termRef.current = term;
      fitRef.current = () => fit.fit();
      fit.fit();

      term.attachCustomKeyEventHandler(() => false);

      const observer = new ResizeObserver(() => fit.fit());
      observer.observe(host);
      observerRef.current = observer;

      const size = { cols: clamp(term.cols, 2, 500), rows: clamp(term.rows, 1, 200) };
      const opened = await api.openTerminal(authorizationId, size);
      sentSizeRef.current = size;
      if (!mountedRef.current) {
        void release(opened.sessionId);
        return;
      }
      sessionIdRef.current = opened.sessionId;
      setStage('live');

      const sendResize = (cols: number, rows: number) => {
        const next = { cols: clamp(cols, 2, 500), rows: clamp(rows, 1, 200) };
        sentSizeRef.current = next;
        send({ type: 'resize', ...next });
      };
      const send = (frame: object) => {
        if (socketRef.current === socket && !socketClosedRef.current)
          socket.send(JSON.stringify(frame));
      };
      const socket = api.terminalSocket(opened.webSocketPath);
      socketRef.current = socket;
      socketClosedRef.current = false;

      term.onResize(({ cols, rows }) => {
        if (readyRef.current) sendResize(cols, rows);
      });

      socket.addEventListener('open', () => send({ type: 'authenticate', ticket: opened.ticket }));
      socket.addEventListener('message', (event: MessageEvent) => {
        if (socketRef.current !== socket || typeof event.data !== 'string') return;
        let frame: Record<string, unknown>;
        try {
          // shortcut: a tool name split across two output frames stays readable; buffer a partial
          // trailing word if that is ever seen.
          const parsed: unknown = JSON.parse(event.data, withoutToolNames);
          if (!parsed || typeof parsed !== 'object') return;
          frame = parsed as Record<string, unknown>;
        } catch {
          return;
        }
        if (frame.type === 'ready') {
          readyRef.current = true;
          everReadyRef.current = true;
          const sent = sentSizeRef.current;
          if (clamp(term.cols, 2, 500) !== sent.cols || clamp(term.rows, 1, 200) !== sent.rows)
            sendResize(term.cols, term.rows);
          if (!stoppingRef.current) setStatus('Terminal connected');
        } else if (frame.type === 'output' && typeof frame.data === 'string') {
          if (pausedRef.current) {
            const pending = pendingOutputRef.current + frame.data;
            if (pending.length > MAX_PAUSED_OUTPUT) truncatedRef.current = true;
            pendingOutputRef.current = pending.slice(-MAX_PAUSED_OUTPUT);
          } else {
            term.write(frame.data);
          }
        } else if (frame.type === 'exit') {
          endStatusRef.current = exitText(frame);
        } else if (frame.type === 'error') {
          endStatusRef.current =
            typeof frame.message === 'string' && frame.message ? frame.message : 'Terminal error.';
        }
      });
      socket.addEventListener('close', (event: CloseEvent) => {
        if (socketRef.current !== socket) return;
        socketClosedRef.current = true;
        readyRef.current = false;
        term.options.disableStdin = true;
        if (stoppingRef.current) return;
        if (!everReadyRef.current) void release(opened.sessionId);
        finish(endStatusRef.current ?? `Terminal connection closed (code ${event.code}).`);
      });
    } catch (e) {
      if (!mountedRef.current) return;
      if (sessionIdRef.current) void release(sessionIdRef.current);
      teardown();
      endedRef.current = true;
      setError(message(e));
      setStatus('');
      setStage(hadEnded ? 'ended' : 'idle');
    }
  };

  const stop = () => {
    stoppingRef.current = true;
    setStopping(true);
    setStoppedByUser(true);
    setError(null);
    const attempt = async () => {
      try {
        await api.stopTerminal(authorizationId, sessionIdRef.current);
        if (mountedRef.current) finish('Terminal stopped.');
      } catch (e) {
        if (!mountedRef.current) return;
        if (e instanceof ApiError && e.status === 503 && e.code === 'terminal_stopping') {
          stopTimerRef.current = setTimeout(attempt, (e.retryAfterSeconds ?? 3) * 1000);
          return;
        }
        stoppingRef.current = false;
        setStopping(false);
        if (socketClosedRef.current)
          setStatus(endStatusRef.current ?? 'Terminal connection closed.');
        setError(message(e));
      }
    };
    void attempt();
  };

  let note: string | null = null;
  if (stage === 'idle') {
    if (!capability || phase !== 'running' || capability.reason === 'container_not_running') {
      note =
        phase === 'creating'
          ? 'Preparing your workspace container. The terminal will be available when it is ready.'
          : phase === 'running'
            ? 'Checking terminal availability…'
            : 'The terminal is waiting for a running workspace container.';
    } else if (!capability.available) {
      note =
        capability.reason === 'security_not_verified'
          ? 'The terminal opens after the workspace security check finishes.'
          : `Terminal unavailable (${capability.reason ?? 'unknown'}).`;
    } else if (capability.state === 'connecting' || capability.state === 'connected') {
      note = 'A terminal is already open for this workspace.';
    } else if (capability.state === 'stopping') {
      note = 'A terminal is stopping. Try again in a moment.';
    }
  }

  let statusWord: string;
  if (stopping) {
    statusWord = 'Disconnecting';
  } else if (stage === 'ended') {
    statusWord = stoppedByUser ? 'Disconnected' : 'Session ended';
  } else if (stage === 'live') {
    if (isPaused) {
      statusWord = 'View paused';
    } else if (status === 'Terminal connected') {
      statusWord = 'Terminal connected';
    } else {
      statusWord = status || 'Connecting';
    }
  } else if (stage === 'opening') {
    statusWord = 'Connecting';
  } else if (activityStatus) {
    statusWord = activityStatus.label;
  } else if (phase === 'creating') {
    statusWord = 'Preparing container';
  } else {
    statusWord = 'Waiting for you';
  }

  const isConnected = stage === 'live' && status === 'Terminal connected' && !stopping && !isPaused;

  const currentStep =
    stage === 'ended' ? 6 : stage === 'live' ? 3 : (pipelineStep ?? (phase === 'running' ? 2 : 1));
  // Setup lines belong before the scan they prepared. A container that was recreated after the
  // latest runner event is stamped later than all of it and would sit stuck at the bottom.
  const lastActivityAt = Math.max(
    ...activityEntries.map((entry) => Date.parse(entry.at ?? '')).filter(Number.isFinite),
  );
  const setupEntries: StreamLogEntry[] = (container?.stages ?? []).flatMap((step) =>
    step.at && !(Number.isFinite(lastActivityAt) && Date.parse(step.at) > lastActivityAt)
      ? [
          {
            id: `setup-${step.name}`,
            at: step.at,
            time: formatDiscoveryClock(step.at),
            glyph: step.status === 'done' ? '✓' : step.status === 'failed' ? '✕' : '›',
            actor: 'Infra',
            text: step.label,
            tone: step.status === 'failed' ? 'warn' : step.status === 'done' ? 'ok' : undefined,
          },
        ]
      : [],
  );
  const stream = byTime<StreamLogEntry>([...setupEntries, ...activityEntries, ...entries]);
  // A long scan keeps only its newest lines in the DOM; every record stays on the Evidence page.
  // New lines are queued and typed one at a time instead of landing as a block.
  const { rows } = useTypedStream(stream);
  // Only the newest lines are drawn; older ones from the start of the scan open on demand.
  const [windowSize, setWindowSize] = useState(MAX_RENDERED_EVENTS);
  const keepOffsetRef = useRef<number | null>(null);
  const shown = rows.slice(-windowSize);
  const showEarlier = () => {
    // One request at a time: the scroll position is restored once the lines have been drawn.
    if (keepOffsetRef.current !== null) return;
    const log = emptyRef.current;
    if (log) {
      keepOffsetRef.current = log.scrollHeight - log.scrollTop;
      stickRef.current = false;
    }
    setWindowSize((size) => size + MAX_RENDERED_EVENTS);
  };
  // Lines added above must not move what the reader is looking at.
  useLayoutEffect(() => {
    const log = emptyRef.current;
    if (log && keepOffsetRef.current !== null) {
      log.scrollTop = log.scrollHeight - keepOffsetRef.current;
      keepOffsetRef.current = null;
    }
  }, [windowSize]);
  const days = new Set(shown.map(({ entry }) => entry.at?.slice(0, 10)).filter(Boolean));

  // Every render, not just new rows: a line that grows (Thinking → a taller answer) keeps the count.
  useEffect(() => {
    const log = emptyRef.current;
    if (log && stickRef.current) log.scrollTop = log.scrollHeight;
  });

  // Highlighting text copies it, as if Copy had been pressed.
  const copied = useCopyOnSelect(rootRef, () => termRef.current?.getSelection() ?? '');

  return (
    <section
      ref={rootRef}
      className={styles.terminal}
      aria-label="Workspace terminal"
      data-stage={stage}
      data-fixed-scale
    >
      {/* ── Top Bar: macOS window dots, identity lockup, live status, actions & settings ── */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.windowDots} aria-hidden="true">
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.dot} />
          </div>
          <div className={styles.identity}>
            <div className={styles.identityText}>
              <h2 className={styles.visuallyHidden}>Workspace output</h2>
              <span className={styles.workspace} title={`${workspaceName} / ${domain}`}>
                {workspaceName} / {domain}
              </span>
            </div>
            <span className={styles.readOnly}>
              <LockKeyhole size={11} aria-hidden="true" />
              Read-only
            </span>
          </div>
        </div>

        <div className={styles.headerRight}>
          <div
            className={styles.badge}
            data-connected={isConnected || (stage === 'idle' && activityStatus?.live) || undefined}
            data-ended={stage === 'ended' || undefined}
          >
            <span className={styles.connectionDot} aria-hidden="true" />
            <span className={styles.badgeText}>{statusWord}</span>
          </div>

          <div className={styles.actions}>
            {stage !== 'live' && !note && !readOnly && (
              <Button
                ref={actionRef}
                variant="secondary"
                size="small"
                isDisabled={stage === 'opening'}
                onPress={open}
                className={styles.actionBtn}
              >
                {stage === 'opening'
                  ? 'Opening terminal…'
                  : stage === 'ended'
                    ? 'Open again'
                    : 'Open terminal'}
              </Button>
            )}

            {/* A viewer never opens the terminal, so has nothing to pause or disconnect. */}
            {!readOnly && (
              <>
                <Button
                  variant="secondary"
                  size="small"
                  isDisabled={stage !== 'live' || stopping}
                  aria-pressed={isPaused}
                  onPress={togglePause}
                  className={styles.actionBtn}
                >
                  {isPaused ? 'Resume updates' : 'Pause updates'}
                </Button>

                <Button
                  ref={stage === 'live' ? actionRef : undefined}
                  variant="secondary"
                  size="small"
                  isDisabled={stage !== 'live' || stopping}
                  aria-label="Disconnect terminal view"
                  onPress={stop}
                  className={styles.disconnectBtn}
                >
                  <Square size={9} fill="currentColor" aria-hidden="true" />
                  Disconnect
                </Button>
              </>
            )}

            <Button
              variant="secondary"
              size="small"
              aria-label="Display settings"
              aria-expanded={showSettings}
              aria-controls="terminal-display-settings"
              onPress={() => setShowSettings((shown) => !shown)}
              className={styles.settingsBtn}
            >
              <Settings2 size={14} aria-hidden="true" />
            </Button>
          </div>
        </div>
      </header>

      {/* ── Multi-Stage Verification Pipeline Bar ── */}
      <div className={styles.pipelineBar} aria-label="Execution pipeline">
        {PIPELINE_STAGES.map((s) => {
          const isDone = s.id < currentStep;
          const isActive = s.id === currentStep;
          return (
            <div
              key={s.id}
              className={styles.pipelineStep}
              data-done={isDone || undefined}
              data-active={isActive || undefined}
              title={s.label}
            >
              <span className={styles.pipelineBarSegment} />
              <span className={styles.visuallyHidden}>{s.label}</span>
            </div>
          );
        })}
      </div>

      {/* ── Display Settings Overlay ── */}
      <div id="terminal-display-settings" className={styles.settings} hidden={!showSettings}>
        <SelectField
          label="Output text size"
          value={fontSize}
          options={[
            ['12', 'Small · 12px'],
            ['13', 'Default · 13px'],
            ['15', 'Large · 15px'],
          ]}
          onChange={setFontSize}
        />
        <Checkbox isSelected={showTimestamps} onChange={setShowTimestamps}>
          Show setup timestamps (UTC)
        </Checkbox>
        <p className={styles.hint}>Display settings apply to this view only.</p>
      </div>

      {/* ── Main Viewport: The Mirror of Runtime & Output ── */}
      <div className={styles.body} data-timestamps={showTimestamps}>
        {copied && (
          <div role="status" className={styles.copied}>
            <span aria-hidden="true">✓</span> Copied {copied.lines}{' '}
            {copied.lines === 1 ? 'line' : 'lines'} to the clipboard
          </div>
        )}
        {stage === 'idle' && (
          <div
            ref={emptyRef}
            className={styles.empty}
            onScroll={(event) => {
              const log = event.currentTarget;
              stickRef.current = log.scrollHeight - log.scrollTop - log.clientHeight < 32;
              // Reaching the top asks for the next stretch of earlier lines.
              if (log.scrollTop < EARLIER_TRIGGER_PX && shown.length < rows.length) showEarlier();
            }}
            /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
            tabIndex={0}
            role="region"
            aria-label={
              activityEntries.length > 0 ? 'Discovery runner activity' : 'Workspace setup output'
            }
          >
            {stream.length > 0 ? (
              <div
                role="log"
                aria-live="off"
                aria-label={
                  activityEntries.length > 0
                    ? 'Discovery runner activity'
                    : 'Workspace setup activity'
                }
                className={styles.setupLog}
              >
                <ol className={styles.activity}>
                  {shown.length < rows.length && (
                    <li className={styles.day}>
                      <button type="button" className={styles.earlier} onClick={showEarlier}>
                        Show {Math.min(MAX_RENDERED_EVENTS, rows.length - shown.length)} earlier
                        lines ({rows.length - shown.length} before these, from the start of the
                        scan)
                      </button>
                    </li>
                  )}
                  {shown.map(({ entry, length, typing }, index) => {
                    const day = entry.at?.slice(0, 10);
                    const newDay =
                      days.size > 1 && day && day !== shown[index - 1]?.entry.at?.slice(0, 10);
                    return (
                      <Fragment key={entry.id}>
                        {newDay && <li className={styles.day}>{day}</li>}
                        <EventRow entry={entry} length={length} typing={typing} />
                      </Fragment>
                    );
                  })}
                </ol>
              </div>
            ) : (
              <div className={styles.emptyIntro}>
                <p aria-hidden="true">· Workspace output appears here as it happens.</p>
                <p className={styles.visuallyHidden}>
                  Follow workspace output here. Send your questions and instructions to the agent
                  below.
                </p>
              </div>
            )}

            <div className={styles.prompt}>
              <p>{note ?? ''}</p>
              {!note && !readOnly && (
                <p className={styles.visuallyHidden}>
                  Open a read-only view of the workspace terminal stream.
                </p>
              )}
            </div>
          </div>
        )}

        <div
          className={styles.screen}
          hidden={stage === 'idle'}
          aria-label="Read-only workspace output"
        >
          <div ref={hostRef} className={styles.host} />
        </div>
      </div>

      {/* ── Status Bar & Alerts ── */}
      <div className={`${styles.outputStatus} ${error ? styles.hasError : ''}`}>
        <p role="status" className={`${styles.status} ${styles.visuallyHidden}`}>
          {stopping ? 'Stopping terminal…' : status}
        </p>
        {error && (
          <p role="alert" className={styles.problem}>
            {error}
          </p>
        )}
        <p className={styles.visuallyHidden}>
          {isPaused
            ? 'Only this view is paused. Workspace activity continues; resume to catch up.'
            : 'Read-only terminal stream. Keyboard input is never sent to the shell.'}
        </p>
      </div>

      {/* ── Terminal Questions Dock ── */}
      {/* A viewer only watches; the page's one note says so. */}
      {!readOnly && (
        <section className={styles.dock} aria-label="Workspace questions">
          <>
            <div className={styles.chipsRow} aria-label="Suggested workspace questions">
              {WORKSPACE_QUESTIONS.map((item) => (
                <Button
                  key={item.command}
                  variant="secondary"
                  size="small"
                  className={styles.questionChip}
                  onPress={() => {
                    ask(item.text);
                    questionRef.current?.focus();
                  }}
                >
                  {item.text}
                </Button>
              ))}
            </div>
            <form
              className={styles.questionForm}
              onSubmit={(event) => {
                event.preventDefault();
                ask(question);
              }}
            >
              <span className={styles.promptSymbol} aria-hidden="true">
                ›
              </span>
              <input
                ref={questionRef}
                aria-label="Ask about your workspace"
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                maxLength={1000}
                placeholder={
                  workspaceId
                    ? 'Ask anything about this workspace, or type /help'
                    : 'Ask about recorded setup, or type /help'
                }
                autoComplete="off"
                className={styles.questionInput}
              />
              <kbd className={styles.enterKey} aria-hidden="true">
                ↵
              </kbd>
              <Button
                type="submit"
                size="small"
                variant="secondary"
                aria-label="Ask workspace question"
                isDisabled={!question.trim()}
                className={styles.submitArrow}
              >
                <ArrowUp size={14} aria-hidden="true" />
              </Button>
            </form>
          </>
          {answer && (
            <div role="status" aria-label="Workspace answer" className={styles.visuallyHidden}>
              <p>{answer.question}</p>
              <p>{answer.text}</p>
            </div>
          )}
        </section>
      )}
    </section>
  );
}
